import React, { useRef } from 'react';
import { PropTypes } from 'prop-types';
import { useCurrentEmployeeId } from 'common/contexts/CurrentEmployeeContext';
import isAuthEditRequested from 'common/Status/isAuthEditRequested';
import { PrimaryButton } from 'common/TailwindComponents';
import { browserHistory } from 'common/utils/browserHistory';
import EditServiceAuthDisabledToolTip from 'components/ServiceAuthorization/EditServiceAuthDisabledToolTip';
import { isHttpSuccess } from 'src/api/utils/httpStatus';
import { useCreateApprovedServiceAuthorizationEdition } from '../hooks/useUpdateServiceAuthorization';
import ServiceAuthorizationFormModal from './ServiceAuthorizationFormModal';

const EditServiceAuthorizationButton = ({ authorization }) => {
  const modalRef = useRef();
  const currentEmployeeId = useCurrentEmployeeId();
  const editAuthorization = useCreateApprovedServiceAuthorizationEdition();

  const openModal = () => {
    modalRef.current.openDialog();
  };

  const isEditDisabled = isAuthEditRequested(authorization);

  const isSingleDateApproved = authorization.approved_starts_at === authorization.approved_ends_at;

  const initialValues = {
    authorized_amount: authorization.approved_unit_amount || authorization.approved_cents / 100,
    date_type: isSingleDateApproved ? 'single' : 'range',
    single_date: Date.parse(authorization.approved_ends_at) / 1000,
    range_date_start: Date.parse(authorization.approved_starts_at) / 1000,
    range_date_end: Date.parse(authorization.approved_ends_at) / 1000,
    payer_authorization_number: authorization.payer_authorization_number || '',
    reviewer_note: authorization.reviewer_note || '',
  };

  const handleSubmit = async (formData) => {
    try {
      const response = await editAuthorization({
        authorization,
        reviewer: currentEmployeeId,
        requester: currentEmployeeId,
        ...formData,
      });
      if (isHttpSuccess(response.status)) {
        const newAuthId = response.data.data.id;
        browserHistory.push(`/authorizations/${newAuthId}`);
      }
    } catch (error) {
      // Prevent unhandled error - toast notification is shown by hook's onError callback
    }
  };

  return (
    <div>
      <EditServiceAuthDisabledToolTip disabled={isEditDisabled} position="bottom-end">
        <PrimaryButton
          ariaLabel="edit authorization"
          dataTestId="edit-authorization-button"
          disabled={isEditDisabled}
          icon="Pencil"
          label="Edit"
          onClick={openModal}
        />
      </EditServiceAuthDisabledToolTip>

      <ServiceAuthorizationFormModal
        authorization={authorization}
        modalRef={modalRef}
        title={`Edit Authorization #${authorization.short_id}`}
        confirmLabel="Save"
        description={(
          <>
            Update any authorization details below. After you click Save,{' '}
            <strong>
              a new version of the authorization is created and the previous version is voided.
            </strong>
          </>
        )}
        showRequestDetails={false}
        dataTestId="edit-auth-dialog"
        confirmButtonTestId="edit-auth-dialog-confirm-button"
        initialValues={initialValues}
        onSubmit={handleSubmit}
        excludeAuthorizationId={authorization.id}
      />
    </div>
  );
};

EditServiceAuthorizationButton.propTypes = {
  authorization: PropTypes.shape({
    id: PropTypes.string.isRequired,
    fee_schedule_program: PropTypes.shape({
      fee_schedule: PropTypes.shape({
        id: PropTypes.string.isRequired,
      }),
    }).isRequired,
    approved_cents: PropTypes.number,
    approved_starts_at: PropTypes.string.isRequired,
    approved_ends_at: PropTypes.string.isRequired,
    approved_unit_amount: PropTypes.number,
    payer_authorization_number: PropTypes.string,
    reviewer_note: PropTypes.string,
    short_id: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    requested_cents: PropTypes.number,
    requested_unit_amount: PropTypes.number,
    requested_starts_at: PropTypes.string.isRequired,
    requested_ends_at: PropTypes.string.isRequired,
    original_service_authorization: PropTypes.object,
    case: PropTypes.shape({
      id: PropTypes.string.isRequired,
      person: PropTypes.shape({
        id: PropTypes.string.isRequired,
      }),
    }),
    clinical_modifications: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        code: PropTypes.string.isRequired,
      }),
    ),
    lineage: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        state: PropTypes.string.isRequired,
      }),
    ).isRequired,
  }).isRequired,
};

export default EditServiceAuthorizationButton;
